// ReviewControl — the compact, op-aware accept/reject at the change, pure.
//
// .claude/research/design/03: a review control anchored to the changed element, named by
// OUTCOME (never merge jargon), carrying the agent's one-line rationale and the
// grounding chip so the author judges the change AND its evidence in one place.
//
// This is the sharp end of "distinct commands per intent": the control is NOT
// one flag-swapping widget. It is a match over the `DiffOp` discriminant, and
// each op renders ONLY the outcomes legal for it:
//   • add         → Skip / Add
//   • remove      → Keep it / Remove
//   • scalar-edit → Keep current / Use new            (no "Keep both" — the type
//                   has no combine branch, so it CANNOT be offered)
//   • inline-edit → Keep current / Use new            (node-grain text is single-
//                   valued too — same outcomes as a scalar edit, no "Keep both")
//   • list-edit   → Keep current / Use new / Keep both (list-valued ⇒ legal)
//   • move        → Keep order / Apply
// Because a single-correct-answer edit arrives as a ScalarEditOp, "Keep both"
// is unrepresentable there — the illegal state is designed out, not filtered.
//
// The grounding chip is rendered as GroundingEvidence, so the evidence is one
// click away AT the decision point (.claude/research/design/04: grounding travels with the
// proposal). An optional adaptive over-trust cue (.claude/research/design/03, F13–F15) is a
// salient, multi-channel, NON-BLOCKING banner shown when the author has been
// rubber-stamping — text + icon + a nudge to open the evidence; it never blocks
// the buttons and the parent clears it when the author engages.
//
// Pure/props-driven: `proposal` (op + rationale + grounding) in; the chosen
// `ReviewOutcome` reports UP. No state, no I/O; the accept/reject demo lives in
// story useState.

import { Trans, useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Button } from "../../Button/Button.js";
import { GroundingEvidence } from "../../Grounding/GroundingEvidence/GroundingEvidence.js";
import { Icon } from "../../Icon/Icon.js";
import { Text } from "../../Text/Text.js";
import type { DiffOp, PendingProposal, ReviewOutcome } from "../types.js";
import "./ReviewControl.css";

export type ReviewControlProps = {
    readonly proposal: PendingProposal;
    readonly onReview: (outcome: ReviewOutcome) => void;
    // The adaptive over-trust cue: shown when the author is rubber-stamping.
    // Non-blocking; the parent decides when it fires and clears it on engagement.
    readonly overTrustCue?: boolean;
    // Inspect the evidence behind a resolved anchor (same jump the editor uses).
    readonly onJumpToSource?: (anchorIndex: number) => void;
};

// The outcome buttons per op — each a small named component so the mapping from
// op to legal outcomes is structural, not a runtime branch on a flag.
const OutcomeButtons = ({
    op,
    onReview,
}: {
    op: DiffOp;
    onReview: (o: ReviewOutcome) => void;
}): ReactNode => {
    switch (op.type) {
        case "add":
            return (
                <>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onReview({ type: "skip" })}
                    >
                        <Trans>Skip</Trans>
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => onReview({ type: "add" })}
                    >
                        <Trans>Add</Trans>
                    </Button>
                </>
            );
        case "remove":
            return (
                <>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onReview({ type: "keep-it" })}
                    >
                        <Trans>Keep it</Trans>
                    </Button>
                    <Button
                        variant="danger"
                        size="sm"
                        onClick={() => onReview({ type: "remove" })}
                    >
                        <Trans>Remove</Trans>
                    </Button>
                </>
            );
        // scalar-edit and inline-edit share a body: both are single-valued edits
        // whose only outcomes are Keep current / Use new (no "Keep both"). They
        // differ only in HOW the diff renders (string vs RichText tree), which is
        // the InlineDiff's concern, not the outcome control's.
        case "scalar-edit":
        case "inline-edit":
            return (
                <>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onReview({ type: "keep-current" })}
                    >
                        <Trans>Keep current</Trans>
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => onReview({ type: "use-new" })}
                    >
                        <Trans>Use new</Trans>
                    </Button>
                </>
            );
        case "list-edit":
            return (
                <>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onReview({ type: "keep-current" })}
                    >
                        <Trans>Keep current</Trans>
                    </Button>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onReview({ type: "use-new" })}
                    >
                        <Trans>Use new</Trans>
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => onReview({ type: "keep-both" })}
                    >
                        <Trans>Keep both</Trans>
                    </Button>
                </>
            );
        case "move":
            return (
                <>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onReview({ type: "keep-order" })}
                    >
                        <Trans>Keep order</Trans>
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => onReview({ type: "apply-move" })}
                    >
                        <Trans>Apply</Trans>
                    </Button>
                </>
            );
        default: {
            const exhaustive: never = op;
            return exhaustive;
        }
    }
};

const cueGlyph = <Icon name="info" />;

export const ReviewControl = ({
    proposal,
    onReview,
    overTrustCue,
    onJumpToSource,
}: ReviewControlProps) => {
    const { t } = useLingui();
    return (
        <fieldset
            className="bilda-review-control"
            aria-label={t`Review the proposed change`}
        >
            {overTrustCue ? (
                // Multi-channel (icon + text), non-blocking; the click-through to
                // the evidence sits right below in the meta row.
                <div className="bilda-review-control__cue" role="status">
                    <span
                        className="bilda-review-control__cue-icon"
                        aria-hidden="true"
                    >
                        {cueGlyph}
                    </span>
                    <Text size="sm">
                        <Trans>
                            You've approved several in a row — take a moment
                            with the evidence for this one.
                        </Trans>
                    </Text>
                </div>
            ) : null}

            {/* The agent's one-line rationale — why this change. Default tone
                (not dim): on the elevated card ground, dim text drops below the
                4.5:1 floor in dark; the rationale is worth reading anyway. */}
            <Text size="sm">{proposal.rationale}</Text>

            {/* The meta row: grounding chip (with evidence one click away) sits
                beside the outcome buttons — evidence at the decision point. */}
            <div className="bilda-review-control__meta">
                <GroundingEvidence
                    state={proposal.grounding}
                    onJump={onJumpToSource}
                />
                <div className="bilda-review-control__actions">
                    <OutcomeButtons op={proposal.op} onReview={onReview} />
                </div>
            </div>
        </fieldset>
    );
};
